import { loadJS, getOmniture } from '../browserUtils';
import { getLoginStatus, getUUID, getUserDetails } from './omnitureHelpers';

class OmnitureService {
  #eventsQueue = [];

  #isOmnitureLoaded = false;

  #isLodingOmniture = false;

  #omniture = null;

  #store = null;

  #linkTrackVars = '';

  #setClickVars = null;

  #setLoadVars = null;

  #branch = null;

  /**
   * @description
   * Fires all the events stored in the queue
   * (All events are stored in the queue untill omniture is loaded)
   */
  #fireEventsInQueue = () => {
    while (this.#eventsQueue.length) {
      const cb = this.#eventsQueue.shift();
      cb();
    }
  };

  /**
   * @description
   * initializes omniture from windows object
   */
  #initializeOmniture = () => {
    this.#omniture = getOmniture();
  };

  /**
   * @description
   * Sets common vars which are used in every event and
   * are not page dependant.
   */
  #setCommonVars = () => {
    this.#omniture.eVar20 = getUUID();
    this.#omniture.eVar39 = getLoginStatus();
    this.#omniture.eVar34 = getUserDetails();
    return this;
  };

  /**
   * @description
   * Loads the omniture js files dynamically
   */
  #load = () => {
    if (this.#isLodingOmniture) {
      return;
    }
    this.#isLodingOmniture = true;
    let loadCount = 0;
    loadJS('https://jsak.mmtcdn.com/seo/common/mbox.js', {
      onLoad: () => {
        loadCount++;
        if (loadCount === 2) {
          this.#isOmnitureLoaded = true;
          this.#fireEventsInQueue();
        }
      },
    });

    loadJS('https://jsak.mmtcdn.com/seo/common/s_code.js', {
      onLoad: () => {
        loadCount++;
        if (loadCount === 2) {
          this.#isOmnitureLoaded = true;
          this.#fireEventsInQueue();
        }
      },
    });

    return this;
  };

  /**
   * @description
   * Sets the inital vars and functions to be used later also
   * loads the JS files for tracking
   *
   * @param {object} initParams
   * @param {function} initParams.setLoadVars - Function which sets the vars and props for load event
   * @param {function} initParams.setClickVars - Function which sets the vars and props for click event
   */
  init = (initParams, { skipLoad } = {}) => {
    this.#setLoadVars = initParams?.setLoadVars;
    this.#setClickVars = initParams?.setClickVars;
    if (!skipLoad) {
      this.#load();
    } else {
      this.#isOmnitureLoaded = true;
    }
  };

  /**
   *
   * @description - sets the current store
   * @param {object} store - current page store
   */
  setStore = store => {
    this.#store = store;
    return this;
  };

  /**
   * @description
   * Adds track vars
   * @param {array} trackVars
   */
  addTrackVars = (trackVars = []) => {
    const currentTrackVars = this.#linkTrackVars ? this.#linkTrackVars.split(',') : [];
    this.#linkTrackVars = [...new Set([...currentTrackVars, ...trackVars])].join(',');

    return this;
  };

  /**
   * @description
   * removes track vars
   * @param {array} trackVars
   */
  removeTrackVars = (trackVars = []) => {
    const currentTrackVars = this.#linkTrackVars.split(',');
    const newTrackVars = currentTrackVars.filter(trackVar => !trackVars?.includes(trackVar));
    this.#linkTrackVars = newTrackVars.join(',');
    return this;
  };

  /**
   * @description
   * Fires load event
   *
   * @param {object} params - params for event
   * @param {function} params.setLoadVars - sets the page specific evars and props
   * (will get omniture object, params and store in the arguments)
   */
  trackLoadEvent = (params = {}) => {
    if (!this.#isOmnitureLoaded) {
      this.#eventsQueue.push(() => this.trackLoadEvent(params));
      return;
    }

    if (!this.#omniture) {
      this.#initializeOmniture();
      if (!this.#omniture) return;
    }

    this.#omniture.linkTrackVars = this.#linkTrackVars;

    /**
     * This is needed to get the latest copy of omniture object
     */
    this.#initializeOmniture();

    this.#setCommonVars();
    this.#setLoadVars?.({ omniture: this.#omniture, params, store: this.#store });

    const s_code = typeof this.#omniture.t === 'function' && this.#omniture.t();
    if (s_code) {
      document.write(s_code);
    }
  };

  /**
   * @description
   * Fires click event
   *
   * @param {object} params - params for event
   * @param {function} params.setClickVars - sets the page specific evars and props
   * (will get omniture object, params and store in the arguments)
   */
  trackClickEvent = (params = {}) => {
    if (!this.#isOmnitureLoaded) {
      this.#eventsQueue.push(() => this.trackLoadEvent(params));
      return;
    }

    if (!this.#omniture) {
      this.#initializeOmniture();
      if (!this.#omniture) return;
    }
    this.#omniture.linkTrackVars = this.#linkTrackVars;

    /**
     * This is needed to get the latest copy of omniture object
     */
    this.#initializeOmniture();

    this.#setCommonVars();
    this.#setClickVars?.({ omniture: this.#omniture, params, store: this.#store });

    /**
     * This is needed to get the latest copy of omniture object
     */
    this.#initializeOmniture();
    typeof this.#omniture.tl === 'function' && this.#omniture.tl(this.#omniture, 'o', params);
  };
}

const OmnitureUtils = new OmnitureService();

export default OmnitureUtils;
